import { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputAutocomplete from 'shared/components/Input/InputAutocomplete';
import { InputAutocompleteOption } from 'shared/components/Input/InputAutocomplete/InputAutocomplete';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { GENERAL_INFO_TITLE, SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT, APISuccessT } from 'shared/models/api';
import { FlowT } from 'shared/models/flows';
import { IPNetworkT } from 'shared/models/ip-networks';
import { SourcesResultT } from 'shared/models/sources';
import { SourcesIdentifiers } from 'shared/models/sources-form';
import api from 'utils/api';

import validator from './FlowsManageData.validator';

type FormValues = {
  name: string;
  sourceId: string;
  sourceName: string;
  poolId: string;
  poolName: string;
  enableSync: boolean;
  enableDtls: boolean;
};
function FlowsManageData() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sourceType, setSourceType] = useState('');
  const history = useHistory();
  const { flowId } = useParams() as RouterParams;
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: {
      errors,
    },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: validator,
  });
  const isEdit = flowId !== undefined;
  const [isLoading, setIsLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      api.flows.getOne(flowId)
        .then((resp: Response) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                toast.error(error?.message);
                setIsLoading(false);
              });
          } else {
            resp.json()
              .then((data: FlowT) => {
                reset(data as any);
                setIsLoading(false);
              });
          }
        });
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    if (formData.name === '' || formData.sourceId === '' || formData.poolId === '') {
      toast.warn('Error: At least one of the fields is empty');
      return;
    }
    setIsSubmitting(true);
    const request = isEdit === false
      ? api.flows.register(formData)
      : api.flows.update(flowId as string, formData);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
          setIsSubmitting(false);
        } else {
          resp.json()
            .then((result: APISuccessT) => {
              toast.success(`Flow has been ${isEdit ? 'edited' : 'created'}`);
              history.push(`/flows/${result.id}`);
              setIsSubmitting(false);
            });
        }
      });
  };

  const formatIPNetworkGroupName = (name: string) => {
    switch (name) {
      case 'V4':
        return 'IPV4';
      case 'V6':
        return 'IPV6';
      default:
        return '· Unknown ·';
    }
  };

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="title__header">
        <h1>Flow · { isEdit ? 'Edit' : 'Create' }</h1>
        <div className="title__header--actions">
          <ButtonSubmitData
            isEdit={isEdit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
      <div className="form scroll">
        <div className="form__section">
          <h3>{ GENERAL_INFO_TITLE }</h3>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Name"
            register={register('name')}
            errorField={errors.name}
          />
        </div>
        <div className="form__section">
          <h3>{ SETTINGS_TITLE }</h3>
          {
          !isEdit
            ? (
              <>
                <InputAutocomplete
                  autocompleteName="source"
                  grouped
                  optionFormatFn={(rawOption: SourcesResultT): InputAutocompleteOption => ({
                    label: rawOption.name,
                    groupBy: SourcesIdentifiers[rawOption.source].displayName ?? '· Others ·',
                    ...rawOption,
                  })}
                  apiCall={api.sources}
                  required
                  onChangeFn={(value: SourcesResultT) => {
                    setValue('sourceId', value?.id);
                    setSourceType(value?.source);
                    if (value?.source === 'IP_DTLS_SOURCE') {
                      setValue('enableDtls', false);
                    }
                  }}
                />
                <InputAutocomplete
                  autocompleteName="ip network"
                  grouped
                  optionFormatFn={(rawOption: IPNetworkT): InputAutocompleteOption => ({
                    label: rawOption.name,
                    groupBy: formatIPNetworkGroupName(rawOption.addressType),
                    ...rawOption,
                  })}
                  apiCall={api.IPNetwork}
                  required
                  onChangeFn={(value: IPNetworkT) => setValue('poolId', value?.id)}
                />
              </>
            )
            : (
              <>
                <InputForm.TextField
                  fullWidth
                  sx={{ m: 1 }}
                  label="Select your source"
                  defaultValue={getValues().sourceName}
                  disabled
                />
                <InputForm.TextField
                  fullWidth
                  sx={{ m: 1 }}
                  label="Select your ip network"
                  defaultValue={getValues().poolName}
                  disabled
                />
              </>
            )
        }
          <InputForm.Checkbox
            name="enableSync"
            control={control}
            sx={{ m: 1 }}
            label="Enable Sync Profile"
            defaultChecked={getValues().enableSync}
            disabled={isEdit}
            helpText="Supports empty gateway mode"
          />
          <InputForm.Checkbox
            name="enableDtls"
            control={control}
            sx={{ m: 1 }}
            label="Enable DTLS Encryption"
            defaultChecked={getValues().enableDtls}
            disabled={isEdit || sourceType === 'IP_DTLS_SOURCE'}
            helpText="Secures the traffic between the device and the IPCore using the DTLS protocol"
          />
        </div>
      </div>
    </form>
  );
}

export default FlowsManageData;
